<template>
  <div class="card">
    <div class="card-body">
      <div v-if="!canAccess" class="alert alert-danger mb-0">دسترسی به شارژ مستقیم سازمان ندارید.</div>
      <template v-else>
        <h5 class="mb-3">شارژ مستقیم سازمان</h5>
        <nav class="nav nav-tabs mb-3" aria-label="بخش‌های شارژ مستقیم سازمان">
          <button type="button" class="nav-link ml-1" :class="{ active: activeTab === 'create' }" @click="activeTab = 'create'">ثبت شارژ مستقیم</button>
          <button type="button" class="nav-link" :class="{ active: activeTab === 'logs' }" @click="showLogs">گزارش برداشت‌ها</button>
        </nav>

        <section v-show="activeTab === 'create'">
          <div class="alert alert-warning">
            پیش از ثبت، سازمان و مبلغ را بررسی کنید. اگر نتیجه انتقال نامشخص بود، پیش از هر اقدام بعدی تراکنش بانکی و گزارش را بررسی کنید.
          </div>
          <Loading v-if="loadingOrganizations || session.state === 'submitting'" />
          <div v-if="organizationError" class="alert alert-danger" role="alert">
            {{ organizationError }}
            <button type="button" class="btn btn-sm btn-outline-danger mr-2" @click="loadOrganizations">دریافت دوباره سازمان‌ها</button>
          </div>
          <div v-if="session.state === 'review'" class="alert alert-warning" role="alert">
            <strong>نتیجه انتقال تأیید نشده است.</strong> {{ session.message }}
            <div>قبل از تلاش دوباره تراکنش بانکی و گزارش برداشت‌ها بررسی شود.</div>
            <button type="button" class="btn btn-sm btn-outline-dark mt-2" @click="showLogs">مشاهده گزارش</button>
          </div>
          <div v-if="session.state === 'success'" class="alert alert-success" role="status">
            {{ session.message }}
            <button type="button" class="btn btn-sm btn-outline-success mr-2" @click="showLogs">مشاهده گزارش</button>
          </div>
          <form class="row" @submit.prevent="submitDebit">
            <div class="col-md-6 form-group">
              <label for="direct-debit-org">سازمان</label>
              <select id="direct-debit-org" v-model="orgId" required class="form-control" :disabled="!canSubmit">
                <option value="">انتخاب سازمان</option>
                <option v-for="org in organizations" :key="org.orgId" :value="org.orgId">{{ org.companyName }}</option>
              </select>
            </div>
            <div class="col-md-6 form-group">
              <label for="direct-debit-amount">مبلغ (ریال)</label>
              <input
                id="direct-debit-amount"
                :value="amount"
                type="text"
                inputmode="numeric"
                dir="ltr"
                class="form-control ltr-input"
                placeholder="مثلاً 1,000,000"
                required
                :disabled="!canSubmit"
                @input="onAmountInput"
                @compositionend="onAmountInput"
              />
            </div>
            <div v-if="formError" class="col-12 alert alert-danger" role="alert">{{ formError }}</div>
            <div class="col-12">
              <button type="submit" class="btn btn-primary" :disabled="!canSubmit">{{ session.state === 'submitting' ? 'در حال ثبت...' : 'بررسی و ثبت' }}</button>
            </div>
          </form>
        </section>

        <section v-show="activeTab === 'logs'">
          <div class="alert alert-warning">وضعیت «نامشخص» و درخواست‌های «در انتظار» قدیمی نیازمند بررسی تراکنش بانکی هستند.</div>
          <div v-if="organizationError" class="alert alert-warning">{{ organizationError }} <button type="button" class="btn btn-sm btn-outline-warning mr-2" @click="loadOrganizations">دریافت دوباره سازمان‌ها</button></div>
          <form class="row" @submit.prevent="searchLogs">
            <div class="col-md-3 form-group">
              <label for="direct-debit-filter-org">سازمان</label>
              <select id="direct-debit-filter-org" v-model="filters.orgId" class="form-control">
                <option value="">همه سازمان‌ها</option>
                <option v-for="org in organizations" :key="org.orgId" :value="org.orgId">{{ org.companyName }}</option>
              </select>
            </div>
            <div class="col-md-3 form-group">
              <label for="direct-debit-user">نام کاربری ثبت‌کننده</label>
              <input id="direct-debit-user" v-model="filters.userName" class="form-control" />
            </div>
            <div class="col-md-3 form-group">
              <label for="direct-debit-status">وضعیت</label>
              <select id="direct-debit-status" v-model="filters.status" class="form-control">
                <option value="">همه وضعیت‌ها</option>
                <option value="Pending">در انتظار</option>
                <option value="Success">موفق</option>
                <option value="Failed">ناموفق</option>
                <option value="Unknown">نامشخص</option>
              </select>
            </div>
            <div class="col-md-3 form-group">
              <label for="direct-debit-per-page">تعداد در صفحه</label>
              <select id="direct-debit-per-page" v-model.number="filters.perPage" class="form-control">
                <option :value="20">۲۰</option><option :value="50">۵۰</option><option :value="100">۱۰۰</option>
              </select>
            </div>
            <div class="col-md-3 form-group">
              <label>از تاریخ ثبت</label>
              <Datepicker simple auto-submit locale="fa" input-format="YYYY-MM-DD" v-model="filters.fromDate" :locale-config="dateLocale" />
            </div>
            <div class="col-md-3 form-group">
              <label>تا تاریخ ثبت</label>
              <Datepicker simple auto-submit locale="fa" input-format="YYYY-MM-DD" v-model="filters.toDate" :locale-config="dateLocale" />
            </div>
            <div class="col-md-3 form-group d-flex align-items-end">
              <button type="submit" class="btn btn-primary" :disabled="loadingLogs">جستجو</button>
            </div>
          </form>
          <div v-if="logsError" class="alert alert-danger" role="alert">{{ logsError }}</div>
          <Loading v-if="loadingLogs" />
          <div v-if="logsLoaded && !loadingLogs && !logsError && !rows.length" class="alert alert-info">گزارشی یافت نشد.</div>
          <div v-if="rows.length" class="table-responsive">
            <table class="table table-bordered table-hover">
              <thead><tr><th>تاریخ ثبت</th><th>سازمان</th><th>مبلغ (ریال)</th><th>کاربر</th><th>IP</th><th>وضعیت</th><th>پیام</th><th>مدت (میلی‌ثانیه)</th><th>جزئیات</th></tr></thead>
              <tbody>
                <template v-for="row in rows " >
                  <tr :key="`row-${row.id}`" :class="{ 'table-warning': row.status === 'Unknown' }">
                    <td dir="ltr">{{ formatDirectDebitDate(row.createdAt) }}</td>
                    <td>{{ organizationName(row.orgId) }}</td>
                    <td>{{ formatAmount(row.amount) }}</td>
                    <td>{{ row.userFullName || '-' }}<small v-if="row.userName" class="d-block" dir="ltr">{{ row.userName }}</small></td>
                    <td dir="ltr">{{ row.clientIp || '-' }}</td>
                    <td><span :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span></td>
                    <td>{{ row.message || '-' }}</td>
                    <td>{{ row.durationMs == null ? '-' : row.durationMs }}</td>
                    <td><button type="button" class="btn btn-sm btn-outline-primary" @click="expandedId = expandedId === row.id ? null : row.id">{{ expandedId === row.id ? 'بستن' : 'نمایش' }}</button></td>
                  </tr>
                  <tr v-if="expandedId === row.id" :key="`details-${row.id}`">
                    <td colspan="9">
                      <div><strong>شناسه لاگ:</strong> {{ row.id }}</div>
                      <div><strong>شناسه سازمان:</strong> <span dir="ltr">{{ row.orgId || '-' }}</span></div>
                      <div><strong>شناسه درخواست:</strong> <span dir="ltr">{{ row.requestId || '-' }}</span></div>
                      <div><strong>مرورگر/کلاینت:</strong> {{ row.userAgent || '-' }}</div>
                      <div><strong>کد HTTP کیف پول:</strong> {{ row.httpStatus == null ? '-' : row.httpStatus }}</div>
                      <div><strong>زمان دریافت نتیجه:</strong> <span dir="ltr">{{ formatDirectDebitDate(row.completedAt) }}</span></div>
                      <div><strong>متن پاسخ کیف پول:</strong> <pre class="mb-1" style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ row.responseBody || '-' }}</pre></div>
                      <div><strong>خطا:</strong> <pre class="mb-0" style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ row.error || '-' }}</pre></div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-if="!logsError && totalCount" class="d-flex align-items-center justify-content-between">
            <span>تعداد کل: {{ totalCount }} — صفحه {{ page }} از {{ totalPages }}</span>
            <div>
              <button type="button" class="btn btn-sm btn-outline-primary ml-2" :disabled="loadingLogs || page <= 1" @click="goToPage(page - 1)">قبلی</button>
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingLogs || page >= totalPages" @click="goToPage(page + 1)">بعدی</button>
            </div>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import {
  responseMessage,
  formatDirectDebitAmountEdit,
  readEnabledOrganizations,
  createDirectDebitSession,
  buildDirectDebitLogsRequest,
  readDirectDebitLogs,
  formatDirectDebitDate,
} from "@/utils/directDebitContract";

const base = "Organizations/OrganizationWithdrawal/";

export default {
  name: "OrganizationDirectDebit",
  data() {
    return {
      activeTab: "create",
      session: { state: "idle", message: "" },
      organizations: [],
      organizationError: "",
      loadingOrganizations: false,
      orgId: "",
      amount: "",
      formError: "",
      filters: { orgId: "", userName: "", status: "", fromDate: "", toDate: "", perPage: 20 },
      appliedFilters: { orgId: "", userName: "", status: "", fromDate: "", toDate: "", perPage: 20 },
      dateLocale: { fa: { displayFormat: "jYYYY/jMM/jDD", lang: { label: "شمسی" } } },
      page: 1,
      rows: [],
      totalCount: 0,
      logsLoaded: false,
      loadingLogs: false,
      logsError: "",
      expandedId: null,
    };
  },
  computed: {
    canAccess() {
      return ["admin", "finance"].includes(this.$store.state.role);
    },
    canSubmit() {
      return this.canAccess && !this.loadingOrganizations && this.organizations.length > 0 && this.session.state === "idle";
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.appliedFilters.perPage));
    },
  },
  created() {
    if (!this.canAccess) return;
    this.session = createDirectDebitSession({ confirm: this.confirmDebit, post: this.postDebit });
    this.loadOrganizations();
  },
  methods: {
    formatDirectDebitDate,
    onAmountInput(event) {
      const input = event.target;
      if (event.isComposing) {
        this.amount = input.value;
        return;
      }
      const { value, caret } = formatDirectDebitAmountEdit(input.value, input.selectionStart);
      this.amount = value;
      input.value = value;
      this.$nextTick(() => {
        if (document.activeElement === input) input.setSelectionRange(caret, caret);
      });
    },
    formatAmount(amount) {
      return typeof amount === "number" ? new Intl.NumberFormat("en-IR").format(amount) : "-";
    },
    organizationName(orgId) {
      const organization = this.organizations.find((item) => item.orgId === orgId);
      return organization ? organization.companyName : orgId || "-";
    },
    statusLabel(status) {
      return { Success: "موفق", Failed: "ناموفق", Unknown: "نامشخص - نیاز به پیگیری", Pending: "در انتظار" }[status] || status || "-";
    },
    statusClass(status) {
      return { Success: "badge badge-success", Failed: "badge badge-danger", Unknown: "badge badge-warning", Pending: "badge badge-secondary" }[status] || "badge badge-secondary";
    },
    async loadOrganizations() {
      if (!this.canAccess || this.loadingOrganizations) return;
      this.loadingOrganizations = true;
      this.organizationError = "";
      try {
        const { data } = await this.$api.get(`${base}EnabledOrganizations`, { SuppressedError: true });
        this.organizations = readEnabledOrganizations(data);
      } catch (error) {
        this.organizations = [];
        this.organizationError = error.response
          ? responseMessage(error.response.data, "دریافت فهرست سازمان‌ها ناموفق بود.")
          : error.message || "دریافت فهرست سازمان‌ها ناموفق بود.";
      } finally {
        this.loadingOrganizations = false;
      }
    },
    async confirmDebit(organization, amount) {
      const result = await this.$swal({
        title: "تأیید شارژ مستقیم سازمان",
        text: `سازمان ${organization.companyName}، مبلغ ${this.formatAmount(amount)} ریال. ثبت شود؟`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "بله، ثبت شود",
        cancelButtonText: "انصراف",
      });
      return !!(result.isConfirmed || result.value);
    },
    async postDebit(payload) {
      const { data } = await this.$api.post(`${base}WithdrawAndChargeCredit`, payload, { SuppressedError: true });
      return data;
    },
    async submitDebit() {
      if (!this.canSubmit) return;
      this.formError = "";
      try {
        await this.session.submit(this.orgId, this.amount, this.organizations);
      } catch (error) {
        this.formError = error.message || "اطلاعات واردشده معتبر نیست.";
      }
    },
    showLogs() {
      this.activeTab = "logs";
      if (!this.loadingLogs) this.loadLogs();
    },
    searchLogs() {
      if (this.loadingLogs) return;
      this.appliedFilters = { ...this.filters };
      this.page = 1;
      this.loadLogs();
    },
    goToPage(page) {
      if (this.loadingLogs || page < 1 || page > this.totalPages) return;
      this.page = page;
      this.loadLogs();
    },
    async loadLogs() {
      if (!this.canAccess || this.loadingLogs) return;
      this.logsError = "";
      this.rows = [];
      this.totalCount = 0;
      this.expandedId = null;
      let payload;
      try {
        payload = buildDirectDebitLogsRequest({ ...this.appliedFilters, page: this.page });
      } catch (error) {
        this.logsError = error.message;
        this.logsLoaded = true;
        return;
      }
      this.loadingLogs = true;
      try {
        const { data } = await this.$api.post(`${base}Logs`, payload, { SuppressedError: true });
        const result = readDirectDebitLogs(data);
        this.rows = result.list;
        this.totalCount = result.totalCount;
      } catch (error) {
        this.logsError = error.response
          ? responseMessage(error.response.data, "دریافت گزارش برداشت ناموفق بود.")
          : error.message || "دریافت گزارش برداشت ناموفق بود.";
      } finally {
        this.loadingLogs = false;
        this.logsLoaded = true;
      }
    },
  },
};
</script>
